import { createFileRoute, Link } from "@tanstack/react-router";
import { getBusinessConfig } from "@/lib/site-config";
import { seoHead } from "@/lib/seo";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";

const TERMS_FAQS = [
  {
    q: "Do I need to pay before you start work on my car?",
    a: "No. We give you a clear written quote first. Work only begins once you've approved it. Payment is due when you collect the vehicle — cash, debit or credit card all accepted at the workshop.",
  },
  {
    q: "What warranty do you offer on repairs?",
    a: "All mechanical repairs come with a 12-month parts and labour warranty. Bodywork carries a 6-month workmanship warranty. Keep your invoice — it's your warranty record.",
  },
  {
    q: "Will you do extra work without telling me first?",
    a: "Never. If we discover something during a service or repair that wasn't on the original quote, we stop, call you, explain it in plain English and only proceed once you've agreed.",
  },
  {
    q: "What happens if my MOT fails?",
    a: "You'll receive a clear pass/fail certificate. If repairs are carried out by us within the DVSA-allowed window (10 working days), the partial retest is free. Failures are explained in plain English so you can make an informed decision.",
  },
  {
    q: "How long can I leave my car after the work is finished?",
    a: "Please collect within 3 working days of our completion call. After that we may charge a small storage fee and we cannot accept liability for theft or damage on the public highway side of the yard.",
  },
  {
    q: "What if I'm not happy with the work?",
    a: "Tell us within 14 days. We'll inspect the vehicle and put right anything that falls below our standard at no extra cost. For MOT-specific disputes you can also contact the DVSA appeals service.",
  },
];

export const Route = createFileRoute("/terms")({
  head: () =>
    seoHead({
      path: "/terms",
      title: "Terms & Conditions | Quotes, Warranty & Payment | Woodseer Autos",
      description:
        "Plain-English terms and conditions for Woodseer Autos: quotes, payment, 12-month repair warranty, MOT retests, vehicle collection, complaints and consumer rights.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Terms & Conditions", path: "/terms" },
      ],
      jsonLd: [faqSchema(TERMS_FAQS) as Record<string, unknown>],
    }),
  component: TermsPage,
});

function TermsPage() {
  const biz = getBusinessConfig();

  return (
    <>
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-4">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Legal</p>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-foreground tracking-tight">Terms & Conditions</h1>
          <p className="mt-2 text-sm text-muted-foreground">Last updated: May 2026 · Reviewed every 12 months.</p>

          <p className="mt-6 text-sm text-foreground/85 leading-relaxed">
            These terms apply whenever you book {biz.name} to carry out MOT testing, servicing, diagnostics, mechanical repairs or bodywork. They sit alongside your statutory rights under the Consumer Rights Act 2015 — they don't replace them. If anything is unclear, just call <a href={biz.phoneHref} className="text-primary font-semibold hover:underline">{biz.phone}</a> before you book.
          </p>

          <div className="mt-10 space-y-7 text-sm text-muted-foreground leading-relaxed">
            <Section n="1" title="Services we provide">
              We carry out MOT testing (DVSA station {biz.vtsNumber}, classes 1, 2, 3, 4, 5 and 7), interim/full/major servicing, engine and electrical diagnostics, mechanical repairs (brakes, clutch, suspension, steering, gearbox, exhaust, cooling) and bodywork (dent, paint and panel repair). All work is carried out by experienced technicians at {biz.address}, {biz.postcode}.
            </Section>

            <Section n="2" title="Quotes and pricing">
              We give every customer a clear, itemised quote before any work begins. Quotes are valid for 14 days. If during a job we find related work that wasn't on the original quote, we stop, call you with a revised quote and only continue once you've approved it. We never carry out additional work without explicit consent.
            </Section>

            <Section n="3" title="Payment">
              Payment is due upon collection of your vehicle unless we've agreed otherwise in writing. We accept cash, debit card and major credit cards in the workshop. We do not store card details on this website.
            </Section>

            <Section n="4" title="Warranty">
              Mechanical repairs are covered by a 12-month parts and labour warranty. Bodywork repairs carry a 6-month workmanship warranty. Wear-and-tear items (brake friction surfaces, tyres, wiper blades, bulbs) are excluded. Your invoice is your warranty record — keep it safe.
            </Section>

            <Section n="5" title="MOT testing">
              MOT tests are carried out under DVSA standards. The result is based solely on the condition of the vehicle on the day of test. A free partial retest is offered if remedial work is carried out by us within the DVSA-allowed 10-working-day window. We do not influence pass/fail outcomes — the test is independent of any sales conversation.
            </Section>

            <Section n="6" title="Vehicle collection">
              Please collect your vehicle within 3 working days of our completion call. After that we reserve the right to charge a reasonable storage fee. We are not liable for damage, theft or loss of vehicles left beyond this period except where caused by our negligence.
            </Section>

            <Section n="7" title="Personal items left in your vehicle">
              Please remove valuables before drop-off. Personal items left in the vehicle remain your responsibility — we are not liable for loss or damage to them.
            </Section>

            <Section n="8" title="Liability">
              Our liability is limited to the cost of the work we have carried out. We are not liable for pre-existing conditions, normal wear and tear, or faults unrelated to the work we performed. Nothing in these terms limits liability for death or personal injury caused by our negligence, or for any other liability that cannot be excluded by law.
            </Section>

            <Section n="9" title="Complaints">
              If you're unhappy with any aspect of our service, contact us within 14 days of work being completed and we'll do our best to resolve it promptly. For MOT-specific disputes you may also contact the DVSA appeals service.
            </Section>

            <Section n="10" title="Cancellation">
              You can cancel a booking by phone or email at any time before work begins, free of charge. If we've already started work or ordered specific parts on your behalf, you'll be charged for time and parts incurred up to the point of cancellation.
            </Section>

            <Section n="11" title="Governing law">
              These terms are governed by the laws of England and Wales. Disputes are subject to the exclusive jurisdiction of the courts of England and Wales.
            </Section>

            <Section n="12" title="Contact">
              Questions about these terms? Call <a href={biz.phoneHref} className="text-primary font-semibold hover:underline">{biz.phone}</a> or email <a href={`mailto:${biz.email}`} className="text-primary font-semibold hover:underline">{biz.email}</a>. See our <Link to="/privacy" className="text-primary font-semibold hover:underline">Privacy Policy</Link> and <Link to="/cookies" className="text-primary font-semibold hover:underline">Cookie Policy</Link> for related information.
            </Section>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={TERMS_FAQS} title="Terms FAQs" />
    </>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-foreground mb-2">{n}. {title}</h2>
      <p>{children}</p>
    </div>
  );
}
